import { StrictMode, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { Camera, Check, ChevronDown, ChevronLeft, ChevronRight, CreditCard, Headphones, ImagePlus, Lightbulb, LogOut, MessageCircle, Sparkles, UserRound, X } from "lucide-react";
import { requestTossCardPayment } from "../adapters/toss-client";
import {
  checkLoginId,
  findLoginId,
  getKakaoAuthorizeUrl,
  loginWithEmail,
  loginWithKakao,
  resetPassword,
  sendEmailVerification,
  signupWithEmail,
  verifyEmailCode,
} from "../shared/auth-api";
import {
  analyzeFridgeImages,
  createRecommendation,
  getRecommendation,
  listRecommendations,
  type RecommendationListItem,
  type RecommendationResult,
} from "../shared/recommendation-api";
import {
  cancelMySubscription,
  confirmPayment,
  createPaymentOrder,
  createMemberInquiry,
  deleteSavedRecipe,
  getMemberInquiry,
  getMemberPayment,
  getMyMember,
  getMyProfileImage,
  listMemberInquiries,
  listMemberPayments,
  listSubscriptionPlans,
  recordPaymentFailure,
  listSavedRecipes,
  saveRecipe,
  startSubscriptionTrial,
  updateMyMember,
  updateMyProfile,
  uploadMyProfileImage,
  withdrawMyMember,
  type MemberInquiryView,
  type MemberPaymentView,
  type MemberView,
  type SubscriptionPlanView,
  type SavedRecipeView,
} from "../shared/member-api";
import { clearPendingFridgeImages, loadPendingFridgeImages, savePendingFridgeImages } from "../shared/pending-upload";
import "../styles.css";

const logoSegments = [
  { src: "/assets/logo-0.svg", className: "segment-0" },
  { src: "/assets/logo-1.svg", className: "segment-1" },
  { src: "/assets/logo-2.svg", className: "segment-2" },
  { src: "/assets/logo-3.svg", className: "segment-3" },
  { src: "/assets/logo-4.svg", className: "segment-4" },
  { src: "/assets/logo-5.svg", className: "segment-5" },
  { src: "/assets/logo-6.svg", className: "segment-6" },
  { src: "/assets/logo-7.svg", className: "segment-7a" },
  { src: "/assets/logo-7.svg", className: "segment-7b" },
  { src: "/assets/logo-8.svg", className: "segment-8" },
  { src: "/assets/logo-9.svg", className: "segment-9" },
];

const terms = [
  {
    id: "service",
    label: "[필수] 이용약관 동의",
    required: true,
    description:
      "Better Table 서비스 이용에 필요한 기본 약관입니다. 회원은 서비스의 이용 조건과 운영 정책을 확인하고 이에 동의합니다.",
  },
  {
    id: "privacy",
    label: "[필수] 개인 정보 수집 및 이용",
    required: true,
    description:
      "회원가입과 서비스 제공을 위해 이름, 아이디, 이메일, 휴대전화번호 등 필요한 정보를 수집하며 관련 법령에 따라 안전하게 관리합니다.",
  },
  {
    id: "delegation",
    label: "[필수] 개인 정보 취급 위탁",
    required: true,
    description:
      "원활한 서비스 제공을 위해 인증, 결제, 인프라 운영 등 일부 업무를 전문 사업자에게 위탁할 수 있습니다.",
  },
  {
    id: "marketing",
    label: "[선택] 마케팅 정보 활용",
    required: false,
    description: "신규 기능, 이벤트 및 맞춤형 혜택 안내를 위해 연락처와 서비스 이용 정보를 활용할 수 있습니다.",
  },
  {
    id: "location",
    label: "[선택] 위치 기반 수집 동의",
    required: false,
    description: "사용자 주변의 식재료 구매처 등 위치 기반 기능을 제공하는 경우 기기 위치 정보를 이용할 수 있습니다.",
  },
] as const;

const emailDomains = ["naver.com", "gmail.com", "daum.net", "직접입력"] as const;
type TermId = (typeof terms)[number]["id"];

function BetterTableLogo({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "bt-home-logo" : "bt-login-logo"} role="img" aria-label="Better Table">
      {logoSegments.map((segment) => (
        <span key={segment.className} className={segment.className}>
          <img src={segment.src} alt="" />
        </span>
      ))}
    </div>
  );
}

function navigate(view?: string, params?: Record<string, string>) {
  const url = new URL(window.location.href);
  url.search = "";
  if (view) url.searchParams.set("view", view);
  Object.entries(params ?? {}).forEach(([key, value]) => url.searchParams.set(key, value));
  window.location.href = `${url.pathname}${url.search}`;
}

const PROFILE_SAVED_KEY = "betterTableProfileSaved";
const POST_PROFILE_TARGET_KEY = "betterTablePostProfileTarget";
const POST_SUBSCRIPTION_TARGET_KEY = "betterTablePostSubscriptionTarget";
const PROFILE_REQUIRED_EVENT = "betterTable:profile-required";
const SUBSCRIPTION_REQUIRED_EVENT = "betterTable:subscription-required";
const LOOKUP_EMAIL_KEY = "betterTableLookupEmail";
const FOUND_LOGIN_ID_KEY = "betterTableFoundLoginId";

function storeMember(member: Pick<MemberView, "memberCode" | "loginId" | "name" | "email" | "phone"> & Partial<Pick<MemberView, "profileImageKey">>) {
  try {
    const current = JSON.parse(sessionStorage.getItem("betterTableMemberSession") ?? "{}");
    sessionStorage.setItem("betterTableMemberSession", JSON.stringify({ ...current, member }));
  } catch {
    // A subsequent authenticated request restores the member view.
  }
}

function storeProfileRequirement(required: boolean) {
  if (required) localStorage.removeItem(PROFILE_SAVED_KEY);
  else localStorage.setItem(PROFILE_SAVED_KEY, "true");
}

function displayDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("ko-KR", {
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(date).replace(/\. /g, ".").replace(/\.$/, "");
}

function navigateWithProfile(view: string, params?: Record<string, string>) {
  if (localStorage.getItem(PROFILE_SAVED_KEY) === "true") {
    navigate(view, params);
    return;
  }

  const target = new URL(window.location.href);
  target.search = "";
  target.searchParams.set("view", view);
  Object.entries(params ?? {}).forEach(([key, value]) => target.searchParams.set(key, value));
  sessionStorage.setItem(POST_PROFILE_TARGET_KEY, `${target.pathname}${target.search}`);
  window.dispatchEvent(new Event(PROFILE_REQUIRED_EVENT));
}

function hasActiveSubscription(member: MemberView) {
  if (!member.subscriptionEndedAt) return false;
  const endedAt = new Date(member.subscriptionEndedAt).getTime();
  return Number.isFinite(endedAt) && endedAt > Date.now();
}

async function navigateWithRecommendationAccess(view: string, params?: Record<string, string>) {
  try {
    const member = await getMyMember();
    storeMember(member);
    storeProfileRequirement(!member.profileCompletedAt);
    if (!member.profileCompletedAt) {
      navigateWithProfile(view, params);
      return;
    }
    if (!hasActiveSubscription(member)) {
      const target = new URL(window.location.href);
      target.search = "";
      target.searchParams.set("view", view);
      Object.entries(params ?? {}).forEach(([key, value]) => target.searchParams.set(key, value));
      sessionStorage.setItem(POST_SUBSCRIPTION_TARGET_KEY, `${target.pathname}${target.search}`);
      window.dispatchEvent(new Event(SUBSCRIPTION_REQUIRED_EVENT));
      return;
    }
    navigate(view, params);
  } catch (reason) {
    window.alert(reason instanceof Error ? reason.message : "회원 정보를 확인하지 못했습니다.");
  }
}

async function startNewRecommendation() {
  sessionStorage.removeItem("betterTableIngredients");
  sessionStorage.removeItem("betterTableImageAnalysis");
  sessionStorage.removeItem("betterTableRecommendationResult");
  pendingFridgeFiles = [];
  await clearPendingFridgeImages();
  await navigateWithRecommendationAccess("recommendProfile");
}

function ActionModal({ title, body, cancelLabel, confirmLabel = "확인", onCancel, onConfirm }: {
  title: ReactNode;
  body?: ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  onCancel?: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="bt-modal-layer" role="dialog" aria-modal="true" aria-label="안내">
      <div className="bt-action-modal">
        <div className="bt-action-modal-header">
          <h2>{title}</h2>
          {body && <p>{body}</p>}
        </div>
        <div className="bt-action-modal-buttons">
          {cancelLabel && <button type="button" className="is-cancel" onClick={onCancel}>{cancelLabel}</button>}
          <button type="button" className="is-confirm" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function ProfileGuardModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(PROFILE_REQUIRED_EVENT, show);
    return () => window.removeEventListener(PROFILE_REQUIRED_EVENT, show);
  }, []);

  if (!open) return null;
  return (
    <ActionModal
      title={<>프로필 설정 후<br />이용해 주세요.</>}
      cancelLabel="취소"
      onCancel={() => {
        sessionStorage.removeItem(POST_PROFILE_TARGET_KEY);
        setOpen(false);
      }}
      onConfirm={() => navigate("healthProfile")}
    />
  );
}

function SubscriptionGuardModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, show);
    return () => window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, show);
  }, []);

  if (!open) return null;
  return (
    <ActionModal
      title={<>구독 후 AI 추천을<br />이용해 주세요.</>}
      body="이용 중인 구독권이 없거나 구독 기간이 만료되었습니다."
      cancelLabel="취소"
      confirmLabel="구독하기"
      onCancel={() => {
        sessionStorage.removeItem(POST_SUBSCRIPTION_TARGET_KEY);
        setOpen(false);
      }}
      onConfirm={() => navigate("subscription")}
    />
  );
}

function MobileTopbar({ title, backTo, backParams }: { title: string; backTo?: string; backParams?: Record<string, string> }) {
  return (
    <header className="bt-mobile-topbar">
      <button type="button" className="bt-back-button" onClick={() => navigate(backTo, backParams)} aria-label="이전 화면으로 돌아가기">
        <ChevronLeft aria-hidden="true" size={24} strokeWidth={1.8} />
      </button>
      <strong>{title}</strong>
    </header>
  );
}

function AuthScreen({
  title,
  backTo,
  backParams,
  children,
}: {
  title: string;
  backTo?: string;
  backParams?: Record<string, string>;
  children: ReactNode;
}) {
  return (
    <main className="bt-app-frame">
      <section className="bt-signup-screen">
        <MobileTopbar title={title} backTo={backTo} backParams={backParams} />
        {children}
      </section>
    </main>
  );
}

function FullButton({ children, disabled = false, type = "button", onClick }: {
  children: ReactNode;
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
}) {
  return (
    <div className="bt-full-button-wrap">
      <button className="bt-full-button" type={type} disabled={disabled} onClick={onClick}>
        {children}
      </button>
    </div>
  );
}

function LoginScreen() {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [kakaoLoading, setKakaoLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!loginId.trim() || !password) return;
    setLoginLoading(true);
    setError("");
    try {
      const result = await loginWithEmail(loginId, password);
      sessionStorage.setItem("betterTableMemberSession", JSON.stringify(result));
      storeProfileRequirement(result.profileRequired);
      navigate("home");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "로그인에 실패했습니다.");
    } finally {
      setLoginLoading(false);
    }
  }

  return (
    <main className="bt-app-frame">
      <section className="bt-login-screen" aria-labelledby="login-title">
        <h1 id="login-title" className="bt-visually-hidden">Better Table 로그인</h1>
        <BetterTableLogo />
        <form className="bt-login-form" onSubmit={handleSubmit}>
          <div className="bt-login-inputs">
            <label className="bt-visually-hidden" htmlFor="login-id">아이디</label>
            <input id="login-id" autoComplete="username" value={loginId} onChange={(event) => setLoginId(event.target.value)} placeholder="아이디를 입력해 주세요." />
            <label className="bt-visually-hidden" htmlFor="login-password">비밀번호</label>
            <input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력해 주세요." />
          </div>
          <button className="bt-primary-button" type="submit" disabled={loginLoading || kakaoLoading}>
            {loginLoading ? "로그인 중" : "로그인"}
          </button>
          <nav className="bt-account-links" aria-label="계정 메뉴">
            <a href="?view=findId">아이디 찾기</a><span aria-hidden="true">|</span>
            <a href="?view=findPassword">비밀번호 찾기</a><span aria-hidden="true">|</span>
            <a href="?view=signup">회원가입</a>
          </nav>
          <div className="bt-login-divider"><span>또는</span></div>
          <button className="bt-kakao-button" type="button" disabled={kakaoLoading} onClick={async () => {
            setKakaoLoading(true);
            setError("");
            try {
              const { authorizeUrl } = await getKakaoAuthorizeUrl();
              window.location.assign(authorizeUrl);
            } catch (reason) {
              setError(reason instanceof Error ? reason.message : "카카오 로그인을 시작하지 못했습니다.");
              setKakaoLoading(false);
            }
          }}><MessageCircle aria-hidden="true" size={20} fill="currentColor" />{kakaoLoading ? "연결 중" : "카카오로 시작하기"}</button>
          {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
        </form>
      </section>
    </main>
  );
}

function KakaoCallbackScreen() {
  const [error, setError] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const kakaoError = params.get("error_description") ?? params.get("error");
    if (kakaoError || !code) {
      setError(kakaoError || "카카오 인증 코드를 확인할 수 없습니다.");
      return;
    }
    void loginWithKakao(code).then((result) => {
      sessionStorage.setItem("betterTableMemberSession", JSON.stringify(result));
      storeProfileRequirement(result.profileRequired);
      window.location.replace(result.accountInfoRequired ? "?view=myInfo&source=kakao" : "?view=home");
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "카카오 로그인에 실패했습니다."));
  }, []);
  return (
    <main className="bt-app-frame">
      <section className="bt-auth-callback" aria-live="polite">
        <BetterTableLogo />
        {error ? <><p role="alert">{error}</p><a href="?">로그인으로 돌아가기</a></> : <p>카카오 로그인 정보를 확인하고 있습니다.</p>}
      </section>
    </main>
  );
}

function Checkbox({ checked, size = 20 }: { checked: boolean; size?: 20 | 24 }) {
  return (
    <span className={`bt-checkbox ${checked ? "is-checked" : ""} size-${size}`} aria-hidden="true">
      {checked ? <img src={`/assets/check-${size}.svg`} alt="" /> : <img src={`/assets/checkbox-outline-${size}.svg`} alt="" />}
    </span>
  );
}

function SignupTermsScreen() {
  const [checked, setChecked] = useState<Set<TermId>>(new Set());
  const [expanded, setExpanded] = useState<TermId | null>(null);
  const allChecked = checked.size === terms.length;
  const requiredChecked = terms.filter((term) => term.required).every((term) => checked.has(term.id));

  function toggleAll() {
    setChecked(allChecked ? new Set() : new Set(terms.map((term) => term.id)));
  }

  function toggleTerm(id: TermId) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <AuthScreen title="회원가입">
      <h1 className="bt-signup-heading">서비스 이용을 위해<br />이용약관 동의가 필요합니다.</h1>
      <div className="bt-terms-panel">
        <button type="button" className={`bt-all-terms ${allChecked ? "is-checked" : ""}`} onClick={toggleAll} aria-pressed={allChecked}>
          <Checkbox checked={allChecked} size={24} /><span>약관에 전체 동의합니다.</span>
        </button>
        <ul className="bt-term-list">
          {terms.map((term) => {
            const isChecked = checked.has(term.id);
            const isExpanded = expanded === term.id;
            return (
              <li key={term.id} className={isExpanded ? "is-expanded" : ""}>
                <div className="bt-term-row">
                  <button type="button" className="bt-term-check" onClick={() => toggleTerm(term.id)} aria-pressed={isChecked}>
                    <Checkbox checked={isChecked} />
                    <span className={`bt-term-type ${term.required ? "is-required" : ""}`}>[{term.required ? "필수" : "선택"}]</span>
                    <span className="bt-term-label">{term.label.replace(/^\[[^\]]+\]\s*/, "")}</span>
                  </button>
                  <button type="button" className="bt-term-expand" onClick={() => setExpanded(isExpanded ? null : term.id)} aria-expanded={isExpanded} aria-label={`${term.label} 내용 ${isExpanded ? "닫기" : "보기"}`}>
                    <img src="/assets/chevron-down.svg" alt="" />
                  </button>
                </div>
                {isExpanded && <p className="bt-term-description">{term.description}</p>}
              </li>
            );
          })}
        </ul>
      </div>
      <FullButton disabled={!requiredChecked} onClick={() => navigate("signupProfile")}>다음</FullButton>
    </AuthScreen>
  );
}

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function SignupProfileScreen() {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [idChecked, setIdChecked] = useState(false);
  const [idCheckLoading, setIdCheckLoading] = useState(false);
  const [idCheckMessage, setIdCheckMessage] = useState("");
  const idValid = /^[A-Za-z0-9]{8,20}$/.test(userId);
  const passwordValid = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/.test(password);
  const formValid = idValid && idChecked && passwordValid && password === passwordConfirm && name.trim().length > 0 && phone.replace(/\D/g, "").length === 11;

  async function checkId() {
    if (!idValid) return;
    setIdCheckLoading(true);
    setIdCheckMessage("");
    try {
      const result = await checkLoginId(userId);
      setIdChecked(result.available);
      setIdCheckMessage(result.available ? "사용 가능한 아이디입니다." : "이미 사용 중인 아이디입니다.");
    } catch (reason) {
      setIdChecked(false);
      setIdCheckMessage(reason instanceof Error ? reason.message : "중복확인을 완료하지 못했습니다.");
    } finally {
      setIdCheckLoading(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!formValid) return;
    sessionStorage.setItem("betterTableSignupProfile", JSON.stringify({ userId, password, name, phone }));
    navigate("signupEmail");
  }

  return (
    <AuthScreen title="회원가입" backTo="signup">
      <form className="bt-auth-form" onSubmit={submit}>
        <h1 className="bt-signup-heading">회원님의 기본 정보를<br />입력해 주세요.</h1>
        <div className="bt-form-content bt-profile-fields">
          <label className="bt-field-group">
            <span>아이디</span>
            <div className="bt-inline-field">
              <input value={userId} onChange={(event) => { setUserId(event.target.value.replace(/[^A-Za-z0-9]/g, "")); setIdChecked(false); setIdCheckMessage(""); }} placeholder="아이디" />
              <button type="button" className="bt-secondary-button" onClick={checkId} disabled={!idValid || idCheckLoading}>{idCheckLoading ? "확인 중" : "중복확인"}</button>
            </div>
            <small className={idChecked ? "is-success" : idCheckMessage ? "is-error" : ""}>{idCheckMessage || "아이디는 영문, 숫자 8~20자 이내로 입력해주세요."}</small>
          </label>
          <label className="bt-field-group"><span>비밀번호</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력해 주세요." /><input type="password" value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} placeholder="비밀번호를 입력해 주세요." /></label>
          <label className="bt-field-group"><span>이름</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="이름" /></label>
          <label className="bt-field-group"><span>휴대전화 번호</span><input inputMode="numeric" value={phone} onChange={(event) => setPhone(formatPhone(event.target.value))} placeholder="휴대전화 번호를 숫자만 입력해 주세요." /></label>
        </div>
        <FullButton type="submit" disabled={!formValid}>다음</FullButton>
      </form>
    </AuthScreen>
  );
}

function SignupEmailScreen() {
  const [local, setLocal] = useState("");
  const [domainOption, setDomainOption] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const domain = domainOption === "직접입력" ? customDomain : domainOption;
  const emailValid = /^[A-Za-z0-9._%+-]+$/.test(local) && /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(domain);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!emailValid) return;
    const email = `${local}@${domain}`;
    setLoading(true);
    setError("");
    try {
      await sendEmailVerification(email, "SIGNUP");
      sessionStorage.setItem("betterTableSignupEmail", email);
      navigate("signupVerify");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "인증번호를 발송하지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthScreen title="회원가입" backTo="signupProfile">
      <form className="bt-auth-form" onSubmit={submit}>
        <h1 className="bt-signup-heading">마지막으로 가입을 위해<br />이메일을 인증해 주세요.</h1>
        <div className="bt-form-content bt-email-content">
          <span className="bt-email-label">이메일</span>
          <div className="bt-email-row">
            <input value={local} onChange={(event) => setLocal(event.target.value)} placeholder="아이디" aria-label="이메일 아이디" />
            <span className="bt-at">@</span>
            <div className="bt-select-wrap">
              <select value={domainOption} onChange={(event) => setDomainOption(event.target.value)} aria-label="이메일 도메인">
                <option value="">선택</option>
                {emailDomains.map((item) => <option key={item}>{item}</option>)}
              </select>
              <ChevronDown size={20} aria-hidden="true" />
            </div>
          </div>
          {domainOption === "직접입력" && <input className="bt-direct-domain" value={customDomain} onChange={(event) => setCustomDomain(event.target.value)} placeholder="도메인을 입력해 주세요." aria-label="직접 입력 도메인" />}
          {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
        </div>
        <FullButton type="submit" disabled={!emailValid || loading}>{loading ? "발송 중" : "인증"}</FullButton>
      </form>
    </AuthScreen>
  );
}

function SignupVerifyScreen() {
  const [code, setCode] = useState("");
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const email = sessionStorage.getItem("betterTableSignupEmail") ?? "입력한 이메일";
  const profile = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("betterTableSignupProfile") ?? "{}"); }
    catch { return {}; }
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (code.length !== 8) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await verifyEmailCode(email, "SIGNUP", code);
      const result = await signupWithEmail({
        loginId: profile.userId,
        password: profile.password,
        name: profile.name,
        phone: profile.phone,
        email,
      });
      sessionStorage.setItem("betterTableMemberSession", JSON.stringify(result));
      storeProfileRequirement(result.profileRequired);
      setComplete(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "이메일 인증을 완료하지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }

  async function resend() {
    setResendLoading(true);
    setError("");
    setMessage("");
    try {
      await sendEmailVerification(email, "SIGNUP");
      setCode("");
      setMessage("인증번호를 다시 발송했습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "인증번호를 다시 발송하지 못했습니다.");
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <AuthScreen title="회원가입" backTo="signupEmail">
      <form className="bt-auth-form" onSubmit={submit}>
        <h1 className="bt-signup-heading">인증번호가 전송되었습니다.<br />인증을 완료해 주세요.</h1>
        <div className="bt-form-content bt-verify-content">
          <label className="bt-field-group"><span>인증번호</span><input inputMode="numeric" maxLength={8} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="8자리 인증번호" /></label>
          <p><strong>{email}</strong>로 전송된 인증번호를 입력해 주세요.</p>
          <button type="button" className="bt-resend-button" onClick={resend} disabled={resendLoading}>{resendLoading ? "재발송 중" : "인증번호 재발송"}</button>
          {message ? <p className="bt-auth-success" role="status">{message}</p> : null}
          {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
        </div>
        <FullButton type="submit" disabled={code.length !== 8 || loading}>{loading ? "확인 중" : "확인"}</FullButton>
      </form>
      {complete && (
        <div className="bt-modal-layer" role="dialog" aria-modal="true" aria-labelledby="signup-complete-title">
          <div className="bt-modal-card">
            <span className="bt-modal-check"><Check size={28} strokeWidth={2.2} /></span>
            <h2 id="signup-complete-title">회원가입이 완료되었습니다.</h2>
            <p>{profile.name ?? "회원"}님의 가입을 축하드립니다.<br />Better Table과 더 나은 식탁을 시작해 보세요.</p>
            <button type="button" onClick={() => {
              sessionStorage.removeItem("betterTableSignupProfile");
              sessionStorage.removeItem("betterTableSignupEmail");
              navigate("home");
            }}>확인</button>
          </div>
        </div>
      )}
    </AuthScreen>
  );
}

function EmailLookupScreen({ mode }: { mode: "id" | "password" }) {
  const [local, setLocal] = useState("");
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const title = mode === "id" ? "아이디 찾기" : "비밀번호 찾기";
  const valid = local.length > 0 && domain.length > 0;
  return (
    <AuthScreen title={title}>
      <form className="bt-auth-form" onSubmit={(event) => event.preventDefault()}>
        <h1 className="bt-signup-heading">회원가입 시 입력한<br />이메일을 인증해 주세요.</h1>
        <div className="bt-form-content bt-email-content">
          <span className="bt-email-label">이메일</span>
          <div className="bt-email-row">
            <input value={local} onChange={(event) => setLocal(event.target.value)} placeholder="아이디" />
            <span className="bt-at">@</span>
            <div className="bt-select-wrap">
              <select value={domain} onChange={(event) => setDomain(event.target.value)}><option value="">선택</option>{emailDomains.slice(0, 3).map((item) => <option key={item}>{item}</option>)}</select>
              <ChevronDown size={20} aria-hidden="true" />
            </div>
          </div>
        </div>
        {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
        <FullButton disabled={!valid || loading} onClick={async () => {
          const email = `${local.trim()}@${domain}`;
          setLoading(true); setError("");
          try {
            await sendEmailVerification(email, mode === "id" ? "FIND_ID" : "RESET_PASSWORD");
            sessionStorage.setItem(LOOKUP_EMAIL_KEY, email);
            navigate(mode === "id" ? "findIdVerify" : "findPasswordVerify");
          } catch (reason) { setError(reason instanceof Error ? reason.message : "인증번호를 발송하지 못했습니다."); }
          finally { setLoading(false); }
        }}>{loading ? "발송 중" : "인증"}</FullButton>
      </form>
    </AuthScreen>
  );
}

function LookupVerifyScreen({ mode }: { mode: "id" | "password" }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const email = sessionStorage.getItem(LOOKUP_EMAIL_KEY) ?? "";
  return <AuthScreen title={mode === "id" ? "아이디 찾기" : "비밀번호 찾기"} backTo={mode === "id" ? "findId" : "findPassword"}>
    <div className="bt-lookup-content"><h1>인증번호가 전송되었습니다.<br />인증을 완료해 주세요.</h1><label><span>인증번호</span><span className="bt-code-input"><input inputMode="numeric" maxLength={8} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} /><i>03:00</i></span></label>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}</div>
    <FullButton disabled={code.length !== 8 || loading || !email} onClick={async () => {
      setLoading(true); setError("");
      try {
        await verifyEmailCode(email, mode === "id" ? "FIND_ID" : "RESET_PASSWORD", code);
        if (mode === "id") {
          const result = await findLoginId(email);
          sessionStorage.setItem(FOUND_LOGIN_ID_KEY, result.loginId);
        }
        navigate(mode === "id" ? "findIdResult" : "resetPassword");
      } catch (reason) { setError(reason instanceof Error ? reason.message : "인증번호를 확인해 주세요."); }
      finally { setLoading(false); }
    }}>{loading ? "확인 중" : "확인"}</FullButton>
  </AuthScreen>;
}

function FindIdResultScreen() {
  const loginId = sessionStorage.getItem(FOUND_LOGIN_ID_KEY) ?? "-";
  return <AuthScreen title="아이디 찾기" backTo="findId"><div className="bt-lookup-result"><h1>회원님의 아이디는<br /><strong>{loginId}</strong><br />입니다.</h1></div><div className="bt-dual-full-buttons"><button type="button" onClick={() => navigate()}>로그인</button><button type="button" onClick={() => navigate("findPassword")}>비밀번호 찾기</button></div></AuthScreen>;
}

function ResetPasswordScreen() {
  const [password, setPassword] = useState(""); const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const valid = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/.test(password) && password === confirm;
  return <AuthScreen title="비밀번호 찾기" backTo="findPasswordVerify"><div className="bt-lookup-content"><h1>새로운 비밀번호를<br />입력해 주세요.</h1><div className="bt-reset-fields"><label>비밀번호<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력해 주세요" /></label><input type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} placeholder="비밀번호를 한 번 더 입력해 주세요" /></div>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}</div><FullButton disabled={!valid || loading} onClick={async () => {
    setLoading(true); setError("");
    try { await resetPassword(sessionStorage.getItem(LOOKUP_EMAIL_KEY) ?? "", password); navigate(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "비밀번호를 재설정하지 못했습니다."); }
    finally { setLoading(false); }
  }}>{loading ? "변경 중" : "확인"}</FullButton></AuthScreen>;
}

const analysisSteps = [
  { title: "프로필 분석", description: <>나이, 성별, 체중, 활동량, 건강 목표와<br />질환, 알레르기를 추천 조건으로 반영합니다.</> },
  { title: "냉장고 식재료 분석", description: <>사진으로 보유 재료를 정리하고,<br />직접 확인, 수정한 뒤 추천에 사용</> },
  { title: "맞춤 식단 & 정보 큐레이션", description: <>추천 이유와 영양 정보를<br />누구나 이해하기 쉬운 문장으로 제공</> },
];

type BottomNavigationItem = "홈" | "AI 추천" | "내 레시피" | "마이";

function BottomNavigationIcon({ item, active }: { item: BottomNavigationItem; active: boolean }) {
  const state = active ? "solid" : "stroke";

  if (item === "홈") {
    return (
      <span className={`bt-figma-nav-icon bt-figma-nav-home is-${state}`} aria-hidden="true">
        <img className="bt-home-main" src={`/assets/navigation/home-${state === "solid" ? "main" : "stroke-main"}.svg`} alt="" />
        <img className="bt-home-baseline" src={`/assets/navigation/home-${state === "solid" ? "baseline" : "stroke-baseline"}.svg`} alt="" />
      </span>
    );
  }

  if (item === "AI 추천") {
    const prefix = state === "solid" ? "ai-solid" : "ai";
    return (
      <span className="bt-figma-nav-icon bt-figma-nav-ai" aria-hidden="true">
        <img className="bt-ai-circle" src={`/assets/navigation/${prefix}-circle.svg`} alt="" />
        <img className="bt-ai-a" src={`/assets/navigation/${prefix}-a.svg`} alt="" />
        <img className="bt-ai-i" src={`/assets/navigation/${prefix}-i.svg`} alt="" />
      </span>
    );
  }

  if (item === "내 레시피") {
    return (
      <span className={`bt-figma-nav-icon bt-figma-nav-report is-${state}`} aria-hidden="true">
        {active ? <span className="bt-report-solid-frame" /> : <img className="bt-report-frame" src="/assets/navigation/report-frame.svg" alt="" />}
        <img className="bt-report-line-1" src={`/assets/navigation/report-${active ? "solid-" : ""}line-1.svg`} alt="" />
        <img className="bt-report-line-2" src={`/assets/navigation/report-${active ? "solid-" : ""}line-2.svg`} alt="" />
        <img className="bt-report-line-3" src={`/assets/navigation/report-${active ? "solid-" : ""}line-3.svg`} alt="" />
      </span>
    );
  }

  const prefix = active ? "my-solid" : "my";
  return (
    <span className="bt-figma-nav-icon bt-figma-nav-my" aria-hidden="true">
      <img className="bt-my-circle" src={`/assets/navigation/${prefix}-circle.svg`} alt="" />
      <img className="bt-my-person" src={`/assets/navigation/${prefix}-person.svg`} alt="" />
    </span>
  );
}

function BottomNavigation({ active = "홈" }: { active?: BottomNavigationItem }) {
  const items = [
    { label: "홈", view: "home" },
    { label: "AI 추천", view: "recommend" },
    { label: "내 레시피", view: "savedRecipes" },
    { label: "마이", view: "my" },
  ] satisfies Array<{ label: BottomNavigationItem; view: string }>;
  return (
    <nav className="bt-bottom-nav" aria-label="주요 메뉴">
      {items.map(({ label, view }) => (
        <button key={label} type="button" className={active === label ? "is-active" : ""} onClick={() => view && navigate(view)}>
          <BottomNavigationIcon item={label} active={active === label} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function HomeScreen() {
  const [recentRecipe, setRecentRecipe] = useState<SavedRecipeView | null>(null);
  useEffect(() => { void listSavedRecipes().then((items) => setRecentRecipe(items[0] ?? null)).catch(() => setRecentRecipe(null)); }, []);
  return (
    <main className="bt-app-frame">
      <section className="bt-home-screen">
        <header className="bt-home-topbar"><BetterTableLogo compact /></header>
        <div className="bt-home-content">
          <section className="bt-home-hero">
            <div><h1>오늘의 냉장고엔<br />무엇이 있나요?</h1><p>냉장고 속 재료와 프로필 취향을 분석해<br />오늘의 한끼를 추천합니다.</p></div>
            <button type="button" onClick={() => void navigateWithRecommendationAccess("recommend")}>식단 추천 시작하기</button>
          </section>
          <button type="button" className="bt-subscribe-card" onClick={() => navigate("subscription")}>
            <span><strong>정기 구독하고 AI 식단 추천받자!</strong><u>구독하기</u></span>
            <CreditCard size={42} strokeWidth={1.7} aria-hidden="true" />
          </button>
          <section className="bt-analysis-section">
            <h2>AI가 이렇게 분석합니다.</h2>
            <div className="bt-analysis-list">
              {analysisSteps.map((step, index) => (
                <article key={step.title}>
                  <span>{index + 1}</span>
                  <div><h3>{step.title}</h3><p>{step.description}</p></div>
                </article>
              ))}
            </div>
          </section>
          <section className="bt-recent-section">
            <div className="bt-home-section-title"><h2>최근 저장한 레시피</h2><button type="button" onClick={() => navigate("savedRecipes")}>전체보기</button></div>
            {recentRecipe ? <article className="bt-recipe-card" onClick={() => {
              sessionStorage.setItem("betterTableSelectedSavedRecipe", JSON.stringify(recentRecipe));
              navigate("recipeDetail", { recipe: "0", from: "home", saved: recentRecipe.savedRecipeCode });
            }}><span>{recentRecipe.difficulty}</span><h3>{recentRecipe.name}</h3><p>{displayDate(recentRecipe.savedAt)} <i>|</i> {recentRecipe.kcal}kcal</p></article> : <article className="bt-recipe-card"><h3>저장한 레시피가 없습니다.</h3><p>AI 추천에서 마음에 드는 레시피를 저장해 보세요.</p></article>}
          </section>
        </div>
        <footer className="bt-home-footer">
          <div><strong>주식회사 정단</strong><p>대표이사 : 김영준<br />경기 구리시 한다리길 47-26 (교문동)<br />사업자등록번호 : 649-86-03179</p></div>
          <div><strong>고객센터 (평일 09:00 - 18:00)</strong><p>연락처 : 031-565-5709<br />이메일 : admin@jungdan.com</p></div>
        </footer>
        <BottomNavigation />
      </section>
    </main>
  );
}

function formatRecommendationDate(value: string) {
  return new Intl.DateTimeFormat("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value));
}

function RecommendationScreen() {
  const [history, setHistory] = useState<RecommendationListItem[]>([]);
  useEffect(() => {
    void listRecommendations(2).then(setHistory).catch(() => setHistory([]));
  }, []);
  return (
    <main className="bt-app-frame">
      <section className="bt-tab-screen">
        <header className="bt-tab-topbar"><h1>AI 추천</h1></header>
        <div className="bt-recommend-content">
          <section className="bt-recommend-hero">
            <div><h2>냉장고 속 재료와 취향을 분석해,<br />오늘의 한끼를 추천합니다.</h2><p>식재료를 입력하면, AI 분석을 통해<br />맞춤 레시피를 안내해 드려요.</p></div>
            <button type="button" onClick={() => void startNewRecommendation()}>식단 추천 시작하기</button>
          </section>
          <section className="bt-history-section">
            <div className="bt-home-section-title"><h2>최근 식단 추천 이력</h2><button type="button" onClick={() => navigate("recommendHistory", { from: "recommend" })}>전체보기</button></div>
            <div className="bt-history-list">
              {history.map((item) => (
                <button type="button" key={item.recommendationCode} onClick={() => navigate("recommendResults", { from: "recommend", code: item.recommendationCode })}><strong>{formatRecommendationDate(item.requestedAt)}</strong><p>식재료 : {item.ingredients}</p><span>추천 레시피 {item.recipeCount}개</span></button>
              ))}
              {!history.length ? <p className="bt-empty-copy">최근 식단 추천 이력이 없습니다.</p> : null}
            </div>
          </section>
        </div>
        <BottomNavigation active="AI 추천" />
      </section>
    </main>
  );
}

type UploadPreview = { id: string; src: string; name: string; file: File };
let pendingFridgeFiles: File[] = [];

function RecommendationUploadScreen() {
  const [files, setFiles] = useState<UploadPreview[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [uploadError, setUploadError] = useState("");

  function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const available = 3 - files.length;
    const next = Array.from(fileList).filter((file) => /image\/(jpeg|png)/.test(file.type) && file.size <= 10 * 1024 * 1024).slice(0, available).map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      src: URL.createObjectURL(file),
      name: file.name,
      file,
    }));
    setFiles((current) => [...current, ...next]);
  }

  function removeFile(id: string) {
    setFiles((current) => {
      const removed = current.find((item) => item.id === id);
      if (removed) URL.revokeObjectURL(removed.src);
      return current.filter((item) => item.id !== id);
    });
  }

  async function analyze() {
    setPreparing(true);
    setUploadError("");
    try {
      pendingFridgeFiles = files.map((item) => item.file);
      await savePendingFridgeImages(pendingFridgeFiles);
      navigate("recommendImageLoading");
    } catch (reason) {
      setUploadError(reason instanceof Error ? reason.message : "선택한 이미지를 준비하지 못했습니다.");
      setPreparing(false);
    }
  }

  return (
    <AuthScreen title="정보 입력" backTo="recommend">
      <div className="bt-recommend-upload-content">
        <section className="bt-step-heading"><span>STEP 01.</span><h1>냉장고 속 사진을 등록해 주세요.</h1><p>사진 속 식재료를 AI가 자동으로 정리해 드려요.</p></section>
        <div className="bt-upload-section">
          <div className="bt-upload-card">
            <span className="bt-camera-circle"><Camera size={24} strokeWidth={2} aria-hidden="true" /></span>
            <span className="bt-upload-copy"><strong>냉장고 사진을 등록해 주세요.</strong><small>촬영하거나 앨범에서 최대 3장까지 선택할 수 있어요.</small></span>
            <div className="bt-upload-actions">
              <label className={files.length >= 3 ? "is-disabled" : ""}>
                <input type="file" accept="image/*" capture="environment" onChange={(event) => { addFiles(event.target.files); event.currentTarget.value = ""; }} disabled={files.length >= 3} />
                <Camera size={18} aria-hidden="true" />사진 촬영
              </label>
              <label className={files.length >= 3 ? "is-disabled" : ""}>
                <input type="file" accept="image/jpeg,image/png" multiple onChange={(event) => { addFiles(event.target.files); event.currentTarget.value = ""; }} disabled={files.length >= 3} />
                <ImagePlus size={18} aria-hidden="true" />앨범에서 선택
              </label>
            </div>
          </div>
          <div className="bt-upload-previews">
            {files.map((file) => (
              <figure key={file.id}><img src={file.src} alt={file.name} /><button type="button" onClick={() => removeFile(file.id)} aria-label={`${file.name} 삭제`}><X size={13} /></button></figure>
            ))}
          </div>
        </div>
        {uploadError ? <p className="bt-auth-error" role="alert">{uploadError}</p> : null}
        <p className="bt-upload-guide">- 등록 가능 확장자 jpg/jpeg/png, 파일 최대 용량 10mb입니다.<br />- 재료가 겹치지 않게 촬영하면 인식률이 올라갑니다.<br />- 사진 없이 재료를 직접 입력할 수도 있습니다.</p>
      </div>
      <div className="bt-dual-full-buttons"><button type="button" disabled={!files.length || preparing} onClick={analyze}>{preparing ? "이미지 준비 중" : "다음"}</button><button type="button" disabled={preparing} onClick={async () => { pendingFridgeFiles = []; await clearPendingFridgeImages(); navigate("recommendIngredients"); }}>건너뛰기</button></div>
    </AuthScreen>
  );
}

type DiningPartySize = NonNullable<MemberView["diningPartySize"]>;

type RecommendationInputState = {
  ingredients?: string;
  excluded?: string;
  request?: string;
  diningPartySize?: DiningPartySize;
};

const recommendationPartyLabels: Record<DiningPartySize, string> = {
  ONE: "1인",
  TWO: "2인",
  THREE_TO_FOUR: "3~4인",
  FIVE_PLUS: "5인 이상",
};

function profileExcludedIngredients(value: string | null) {
  const normalized = value?.trim() ?? "";
  return normalized === "해당없음" || normalized === "해당 없음" ? "" : normalized;
}

function mergeProfileExcludedIngredients(profileValue: string | null, currentValue: string) {
  const profileExcluded = profileExcludedIngredients(profileValue);
  if (!profileExcluded || currentValue.includes(profileExcluded)) return currentValue;
  return currentValue.trim() ? `${profileExcluded}, ${currentValue.trim()}` : profileExcluded;
}

function AnalysisLoadingScreen({ type }: { type: "image" | "recommendation" }) {
  const [error, setError] = useState("");
  useEffect(() => {
    if (type === "image") {
      let cancelled = false;
      void (async () => {
        const files = pendingFridgeFiles.length ? pendingFridgeFiles : await loadPendingFridgeImages();
        if (!files.length) throw new Error("선택한 냉장고 사진을 불러오지 못했습니다. 다시 선택해 주세요.");
        return analyzeFridgeImages(files);
      })().then(async (result) => {
        if (cancelled) return;
        const ingredients = result.ingredients.join(", ");
        sessionStorage.setItem("betterTableImageAnalysis", JSON.stringify(result));
        sessionStorage.setItem("betterTableIngredients", JSON.stringify({ ingredients, excluded: "", request: "" }));
        pendingFridgeFiles = [];
        await clearPendingFridgeImages();
        navigate("recommendIngredients");
      }).catch((reason) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "냉장고 사진을 분석하지 못했습니다.");
      });
      return () => { cancelled = true; };
    }
    let cancelled = false;
    let payload: RecommendationInputState;
    try {
      payload = JSON.parse(sessionStorage.getItem("betterTableIngredients") ?? "{}");
    } catch {
      payload = { ingredients: "", excluded: "", request: "" };
    }
    void createRecommendation({
      ingredients: payload.ingredients ?? "",
      excludedIngredients: payload.excluded ?? "",
      request: payload.request ?? "",
      diningPartySize: payload.diningPartySize,
    }).then((result) => {
      if (cancelled) return;
      sessionStorage.setItem("betterTableRecommendationResult", JSON.stringify(result));
      navigate("recommendResults", { code: result.recommendationCode });
    }).catch((reason) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : "AI 추천 결과를 생성하지 못했습니다.");
    });
    return () => { cancelled = true; };
  }, [type]);
  const imageAnalysis = type === "image";
  return (
    <main className="bt-app-frame">
      <section className="bt-loading-screen">
        <div className="bt-loading-center">{error ? <span className="bt-loading-failed"><X size={28} /></span> : <span className="bt-spinner"><Sparkles size={28} /></span>}<h1>{error ? <>{imageAnalysis ? "사진 분석" : "AI 추천"}을 완료하지<br />못했습니다.</> : imageAnalysis ? <>AI가 냉장고 사진을<br />분석하고 있어요...</> : <>AI가 프로필과 식재료를<br />분석하고 있어요...</>}</h1>{error ? <><p className="bt-loading-error" role="alert">{error}</p><button type="button" className="bt-loading-retry" onClick={() => navigate(imageAnalysis ? "recommendProfile" : "recommendIngredients")}>{imageAnalysis ? "사진 다시 선택하기" : "입력 화면으로 돌아가기"}</button></> : null}</div>
        <aside className="bt-analysis-tip"><Lightbulb size={20} aria-hidden="true" /><strong>Tip</strong><p>{imageAnalysis ? <>재료가 겹치지 않게 촬영하면 인식률이 올라갑니다.<br />결과는 직접 수정할 수 있어요.</> : <>AI 추천 결과는 건강한 식생활을 돕는 참고 정보이며,<br />질병의 진단, 치료, 예방 등 의학적 판단을 대체하지 않습니다.</>}</p></aside>
      </section>
    </main>
  );
}

function RecommendationIngredientsScreen() {
  const initial = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("betterTableIngredients") ?? "{}") as RecommendationInputState;
    } catch {
      return {};
    }
  }, []);
  const [ingredients, setIngredients] = useState(initial.ingredients ?? "");
  const [excluded, setExcluded] = useState(initial.excluded ?? "");
  const [request, setRequest] = useState(initial.request ?? "");
  const [diningPartySize, setDiningPartySize] = useState<DiningPartySize | null>(initial.diningPartySize ?? null);
  const [profileError, setProfileError] = useState("");
  useEffect(() => {
    let cancelled = false;
    void getMyMember().then((member) => {
      if (cancelled) return;
      setExcluded((current) => mergeProfileExcludedIngredients(member.allergyFoods, current));
      setDiningPartySize((current) => current ?? member.diningPartySize);
    }).catch((reason) => {
      if (!cancelled) setProfileError(reason instanceof Error ? reason.message : "프로필 정보를 불러오지 못했습니다.");
    });
    return () => { cancelled = true; };
  }, []);
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ingredients.trim() || !diningPartySize) return;
    sessionStorage.setItem("betterTableIngredients", JSON.stringify({ ingredients, excluded, request, diningPartySize }));
    navigate("recommendFinalLoading");
  }
  return (
    <AuthScreen title="정보 입력" backTo="recommendProfile">
      <form className="bt-ingredient-form" onSubmit={submit}>
        <div className="bt-recommend-upload-content">
          <section className="bt-step-heading"><span>STEP 02.</span><h1>식재료를 입력해 주세요.</h1><p>입력한 식재료를 기반으로 AI가 레시피를 추천해 드려요.</p></section>
          <div className="bt-textarea-fields">
            <label><span>냉장고 속 재료 <b>*</b></span><textarea value={ingredients} onChange={(event) => setIngredients(event.target.value)} placeholder="냉장고 속 식재료를 입력해 주세요." /></label>
            <label><span>제외할 재료</span><textarea value={excluded} onChange={(event) => setExcluded(event.target.value)} placeholder="알레르기 또는 제외할 재료를 입력해 주세요." /></label>
            <fieldset className="bt-chip-field bt-recommend-party-field">
              <legend>몇분이서 드실건가요? <b>*</b></legend>
              <div>{(Object.entries(recommendationPartyLabels) as Array<[DiningPartySize, string]>).map(([value, label]) => (
                <button type="button" className={diningPartySize === value ? "is-selected" : ""} key={value} onClick={() => setDiningPartySize(value)}>{label}</button>
              ))}</div>
            </fieldset>
            <label><span>기타 요청사항</span><textarea value={request} onChange={(event) => setRequest(event.target.value)} placeholder="원하는 조리법이나 요청사항을 입력해 주세요." /></label>
          </div>
          {profileError ? <p className="bt-auth-error" role="alert">{profileError}</p> : null}
        </div>
        <FullButton type="submit" disabled={!ingredients.trim() || !diningPartySize}>다음</FullButton>
      </form>
    </AuthScreen>
  );
}

type Recipe = {
  id?: number;
  savedRecipeCode?: string;
  name: string;
  description: string;
  kcal: number;
  carbs: number;
  protein: number;
  fat: number;
  time: string;
  difficulty: "쉬움" | "보통" | "어려움";
  serving: string;
  ingredients: string;
  steps?: string[];
};

const recommendedRecipes: Recipe[] = [
  {
    name: "두부 채소 덮밥",
    description: "보유한 두부와 양배추만으로 부족한 단백질을 채울 수 있어요.",
    kcal: 420,
    carbs: 52,
    protein: 24,
    fat: 12,
    time: "20분",
    difficulty: "쉬움",
    serving: "1인분",
    ingredients: "두부, 양배추, 대파, 계란, 간장",
  },
  {
    name: "돼지고기 카레",
    description: "보유한 돼지고기와 카레가루, 각종 구황작물과 채소로 부족한 영양소와 단백질을 채울 수 있어요.",
    kcal: 520,
    carbs: 52,
    protein: 24,
    fat: 12,
    time: "20분",
    difficulty: "쉬움",
    serving: "1인분",
    ingredients: "두부, 양배추, 대파, 계란, 간장",
  },
  {
    name: "닭가슴살 미역국",
    description: "단백질이 부족한 오늘 식단에, 닭가슴살과 미역으로 20분만에 완성하는 닭가슴살 미역국을 추천드려요.",
    kcal: 420,
    carbs: 52,
    protein: 24,
    fat: 12,
    time: "20분",
    difficulty: "쉬움",
    serving: "1인분",
    ingredients: "닭가슴살, 미역, 코인육수, 간장, 다진마늘",
  },
];

function RecommendationResultsScreen() {
  const params = new URLSearchParams(window.location.search);
  const source = params.get("from");
  const historySource = params.get("historyFrom") ?? "my";
  const code = params.get("code");
  const [result, setResult] = useState<RecommendationResult | null>(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("betterTableRecommendationResult") ?? "null") as RecommendationResult | null;
      return !code || stored?.recommendationCode === code ? stored : null;
    } catch { return null; }
  });
  const [error, setError] = useState("");
  useEffect(() => {
    if (!code || result?.recommendationCode === code) return;
    void getRecommendation(code).then((next) => {
      sessionStorage.setItem("betterTableRecommendationResult", JSON.stringify(next));
      setResult(next);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "추천 결과를 불러오지 못했습니다."));
  }, [code, result?.recommendationCode]);
  const recipes: Recipe[] = (result?.recipes ?? []).map((recipe) => ({
    id: recipe.id,
    name: recipe.name,
    description: recipe.description,
    kcal: recipe.kcal,
    carbs: recipe.carbs,
    protein: recipe.protein,
    fat: recipe.fat,
    time: `${recipe.cookingMinutes}분`,
    difficulty: recipe.difficulty,
    serving: `${recipe.servings}인분`,
    ingredients: recipe.ingredients.join(", "),
    steps: recipe.steps,
  }));
  if (error) return <AuthScreen title="AI 추천 레시피" backTo="recommendIngredients"><div className="bt-result-load-state"><p role="alert">{error}</p><button type="button" onClick={() => navigate("recommendIngredients")}>다시 추천받기</button></div></AuthScreen>;
  if (!result) return <AuthScreen title="AI 추천 레시피" backTo="recommendIngredients"><div className="bt-result-load-state"><p>추천 결과를 불러오고 있습니다.</p></div></AuthScreen>;
  return (
    <AuthScreen
      title="AI 추천 레시피"
      backTo={source === "recommendHistory" ? "recommendHistory" : source === "recommend" ? "recommend" : "recommendIngredients"}
      backParams={source === "recommendHistory" ? { from: historySource } : undefined}
    >
      <div className="bt-result-content">
        <h1>입력한 내용을 기반으로<br />분석한 결과를 확인해 보세요.</h1>
        <section className="bt-recommend-points">
          <h2>레시피 추천 포인트</h2>
          <p>{result.recommendationPoint}</p>
          <div>{result.tags.map((tag) => <span key={tag}>#{tag.replace(/^#/, "")}</span>)}</div>
        </section>
        <section className="bt-recommended-recipes">
          <h2>추천 레시피 <b>{recipes.length}</b></h2>
          <div className="bt-result-recipe-list">
            {recipes.map((recipe, index) => (
              <article key={recipe.name} className="bt-result-recipe-card">
                <h3>{recipe.name} ({recipe.kcal}Kcal)</h3>
                <p className="bt-result-description">{recipe.description}</p>
                <div className="bt-macro-grid">
                  <span>탄수<strong>{recipe.carbs}g</strong></span>
                  <span>단백질<strong>{recipe.protein}g</strong></span>
                  <span>지방<strong>{recipe.fat}g</strong></span>
                </div>
                <dl><div><dt>레시피 특징</dt><dd>{recipe.time} <i>|</i> 난이도 {recipe.difficulty} <i>|</i> {recipe.serving}</dd></div><div><dt>포함 식재료</dt><dd>{recipe.ingredients}</dd></div></dl>
                <button type="button" onClick={() => navigate("recipeDetail", {
                  recipe: String(index),
                  from: "recommendResults",
                  code: result.recommendationCode,
                  resultFrom: source ?? "",
                  historyFrom: historySource,
                })}>레시피 자세히 보기</button>
              </article>
            ))}
          </div>
        </section>
        <aside className="bt-result-tip"><strong>💡Tip</strong><p>{result.tip}</p></aside>
      </div>
      <FullButton onClick={() => navigate("home")}>홈으로</FullButton>
    </AuthScreen>
  );
}

function RecipeDetailScreen() {
  const params = new URLSearchParams(window.location.search);
  const source = params.get("from") || "recommendResults";
  const recommendationCode = params.get("code") ?? "";
  const resultSource = params.get("resultFrom") ?? "";
  const historySource = params.get("historyFrom") ?? "my";
  const recipeIndex = Number(params.get("recipe") ?? 2);
  let aiRecipes: Recipe[] = [];
  let storedSavedRecipe: SavedRecipeView | null = null;
  try {
    const stored = JSON.parse(sessionStorage.getItem("betterTableRecommendationResult") ?? "null") as RecommendationResult | null;
    aiRecipes = (stored?.recipes ?? []).map((item) => ({ id: item.id, name: item.name, description: item.description, kcal: item.kcal,
      carbs: item.carbs, protein: item.protein, fat: item.fat, time: `${item.cookingMinutes}분`, difficulty: item.difficulty,
      serving: `${item.servings}인분`, ingredients: item.ingredients.join(", "), steps: item.steps }));
    storedSavedRecipe = JSON.parse(sessionStorage.getItem("betterTableSelectedSavedRecipe") ?? "null") as SavedRecipeView | null;
  } catch { aiRecipes = []; }
  const savedRecipe: Recipe | null = storedSavedRecipe ? {
    id: storedSavedRecipe.recipeId, savedRecipeCode: storedSavedRecipe.savedRecipeCode,
    name: storedSavedRecipe.name, description: storedSavedRecipe.description, kcal: storedSavedRecipe.kcal,
    carbs: storedSavedRecipe.carbs, protein: storedSavedRecipe.protein, fat: storedSavedRecipe.fat,
    time: `${storedSavedRecipe.cookingMinutes}분`, difficulty: storedSavedRecipe.difficulty,
    serving: `${storedSavedRecipe.servings}인분`, ingredients: storedSavedRecipe.ingredients.join(", "),
    steps: storedSavedRecipe.steps,
  } : null;
  const savedRecipeView = Boolean(params.get("saved")) && Boolean(savedRecipe);
  const recipeSource = savedRecipeView && savedRecipe ? [savedRecipe] : source === "recommendResults" && aiRecipes.length ? aiRecipes : recommendedRecipes;
  const recipe = recipeSource[Number.isFinite(recipeIndex) ? Math.min(recipeIndex, recipeSource.length - 1) : 0];
  const detailRecipe = recipe;
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  async function handleRecipeAction() {
    if (savedRecipeView) {
      if (!detailRecipe.savedRecipeCode) return;
      setSaving(true); setActionError("");
      try { await deleteSavedRecipe(detailRecipe.savedRecipeCode); navigate(source === "home" ? "home" : "savedRecipes"); }
      catch (reason) { setActionError(reason instanceof Error ? reason.message : "레시피를 삭제하지 못했습니다."); setSaving(false); }
      return;
    }
    if (localStorage.getItem(PROFILE_SAVED_KEY) !== "true") {
      navigateWithProfile("recipeDetail", { recipe: String(recipeIndex), from: source });
      return;
    }
    if (!detailRecipe.id) { setActionError("AI 추천으로 생성된 레시피만 저장할 수 있습니다."); return; }
    setSaving(true); setActionError("");
    try { await saveRecipe(detailRecipe.id); setSaved(true); }
    catch (reason) { setActionError(reason instanceof Error ? reason.message : "레시피를 저장하지 못했습니다."); }
    finally { setSaving(false); }
  }
  return (
    <AuthScreen
      title="레시피 상세"
      backTo={source}
      backParams={source === "recommendResults" ? {
        code: recommendationCode,
        ...(resultSource ? { from: resultSource } : {}),
        ...(resultSource === "recommendHistory" ? { historyFrom: historySource } : {}),
      } : undefined}
    >
      <div className="bt-recipe-detail-content">
        <section className="bt-recipe-detail-title"><h1>{detailRecipe.name}</h1><p>{detailRecipe.description}</p></section>
        <div className="bt-recipe-summary">
          <span>조리 시간<strong>{detailRecipe.time}</strong></span><span>난이도<strong>{detailRecipe.difficulty}</strong></span><span>분량<strong>{detailRecipe.serving}</strong></span>
        </div>
        <section className="bt-detail-section"><h2>영양 성분</h2><div className="bt-detail-nutrition"><span>칼로리<strong>{detailRecipe.kcal}kcal</strong></span><span>탄수<strong>{detailRecipe.carbs}g</strong></span><span>단백질<strong>{detailRecipe.protein}g</strong></span><span>지방<strong>{detailRecipe.fat}g</strong></span></div></section>
        <section className="bt-detail-section"><h2>재료</h2><ul>{detailRecipe.ingredients.split(", ").map((item) => <li key={item}>{item}</li>)}</ul></section>
        <section className="bt-detail-section"><h2>조리 방법</h2><ol>{(detailRecipe.steps?.length ? detailRecipe.steps : ["재료를 손질합니다.", "준비한 재료를 순서대로 조리합니다.", "간을 맞추고 완성합니다."]).map((step) => <li key={step}>{step}</li>)}</ol></section>
      </div>
      {actionError ? <p className="bt-auth-error" role="alert">{actionError}</p> : null}
      <FullButton disabled={saving || saved} onClick={handleRecipeAction}>{saving ? "처리 중" : savedRecipeView ? "레시피 삭제" : saved ? "저장 완료" : "레시피 저장"}</FullButton>
    </AuthScreen>
  );
}

function SavedRecipesScreen() {
  const [recipes, setRecipes] = useState<SavedRecipeView[]>([]);
  const [error, setError] = useState("");
  useEffect(() => { void listSavedRecipes().then(setRecipes).catch((reason) => setError(reason instanceof Error ? reason.message : "저장 레시피를 불러오지 못했습니다.")); }, []);
  return (
    <main className="bt-app-frame"><section className="bt-tab-screen bt-saved-screen">
      <header className="bt-tab-topbar"><h1>내 레시피</h1></header>
      <div className="bt-saved-content"><p>총 {recipes.length} 건</p>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}<div className="bt-saved-list">{recipes.map((recipe) => (
        <button type="button" key={recipe.savedRecipeCode} onClick={() => {
          sessionStorage.setItem("betterTableSelectedSavedRecipe", JSON.stringify(recipe));
          navigate("recipeDetail", { recipe: "0", from: "savedRecipes", saved: recipe.savedRecipeCode });
        }}>
          <span className={`difficulty-${recipe.difficulty}`}>{recipe.difficulty}</span><strong>{recipe.name}</strong><p>{displayDate(recipe.savedAt)} <i>|</i> {recipe.kcal}kcal</p>
        </button>
      ))}</div></div>
      <BottomNavigation active="내 레시피" />
    </section></main>
  );
}

function MyScreen() {
  const [dialog, setDialog] = useState<"logout" | "withdraw" | "withdrawComplete" | "subscriptionCancel" | "subscriptionCancelled" | null>(null);
  const [member, setMember] = useState<MemberView | null>(null);
  const [error, setError] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  useEffect(() => { void getMyMember().then((value) => { setMember(value); storeMember(value); }).catch((reason) => setError(reason instanceof Error ? reason.message : "회원 정보를 불러오지 못했습니다.")); }, []);
  const menus = [
    { label: "내 정보 수정", view: "myInfo" },
    { label: "프로필 수정", view: "healthProfile" },
    { label: "식단 추천 내역", view: "recommendHistory" },
    { label: "결제 내역", view: "paymentHistory" },
    { label: "1:1 문의", view: "inquiries" },
  ];
  return (
    <main className="bt-app-frame"><section className="bt-tab-screen bt-my-screen">
      <header className="bt-tab-topbar"><h1>MY</h1></header>
      <div className="bt-my-content">
        <section className="bt-my-profile"><MemberAvatar member={member} /><div><strong>{member?.name ?? "회원"} 님</strong><p>@{member?.loginId ?? member?.email ?? "-"}</p></div></section>
        {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
        <section className="bt-subscription-status"><div><span>구독권 정보</span><strong>{member?.subscriptionStatus !== "NONE" && member?.subscriptionEndedAt ? `~ ${displayDate(member.subscriptionEndedAt)}` : "이용 중인 구독권이 없습니다."}</strong>{member?.subscriptionCancelledAt ? <small>구독취소 완료</small> : null}</div><div className="bt-subscription-actions"><button type="button" onClick={() => navigate("subscription")}>결제하기</button>{member?.subscriptionStatus === "PAID" ? <button type="button" className="is-cancel" onClick={() => setDialog("subscriptionCancel")}>구독취소</button> : null}</div></section>
        <nav className="bt-my-menu">{menus.map((menu) => <button type="button" key={menu.label} onClick={() => navigate(menu.view, menu.view === "recommendHistory" ? { from: "my" } : undefined)}><span>{menu.label}</span><ChevronRight size={22} /></button>)}<button type="button" onClick={() => setDialog("logout")}><span>로그아웃</span><LogOut size={20} /></button><button type="button" onClick={() => setDialog("withdraw")}><span>회원탈퇴</span></button></nav>
      </div>
      <BottomNavigation active="마이" />
      {dialog === "logout" && <ActionModal title="로그아웃 하시겠습니까?" cancelLabel="취소" onCancel={() => setDialog(null)} onConfirm={() => { sessionStorage.removeItem("betterTableMemberSession"); navigate(); }} />}
      {dialog === "withdraw" && <ActionModal title={<>회원탈퇴를<br />진행하시겠습니까?</>} body={withdrawing ? "회원탈퇴를 처리하고 있습니다." : undefined} cancelLabel="취소" onCancel={() => { if (!withdrawing) setDialog(null); }} onConfirm={async () => {
        if (withdrawing) return;
        setWithdrawing(true); setError("");
        try {
          await withdrawMyMember();
          sessionStorage.removeItem("betterTableMemberSession");
          localStorage.removeItem(PROFILE_SAVED_KEY);
          setDialog("withdrawComplete");
        } catch (reason) {
          setError(reason instanceof Error ? reason.message : "회원탈퇴를 처리하지 못했습니다.");
          setDialog(null);
        } finally {
          setWithdrawing(false);
        }
      }} />}
      {dialog === "withdrawComplete" && <ActionModal title="회원탈퇴가 완료되었습니다." onConfirm={() => navigate()} />}
      {dialog === "subscriptionCancel" && <ActionModal title={<>이용 중인 구독권을<br />취소하시겠습니까?</>} body={cancellingSubscription ? "구독 취소를 처리하고 있습니다." : "취소 즉시 구독 서비스 이용이 종료됩니다. 결제금액 환불은 별도 절차입니다."} cancelLabel="취소" confirmLabel="구독취소" onCancel={() => { if (!cancellingSubscription) setDialog(null); }} onConfirm={async () => {
        if (cancellingSubscription) return;
        setCancellingSubscription(true); setError("");
        try {
          await cancelMySubscription();
          const updated = await getMyMember();
          setMember(updated); storeMember(updated);
          setDialog("subscriptionCancelled");
        } catch (reason) {
          setError(reason instanceof Error ? reason.message : "구독을 취소하지 못했습니다.");
          setDialog(null);
        } finally {
          setCancellingSubscription(false);
        }
      }} />}
      {dialog === "subscriptionCancelled" && <ActionModal title="구독 취소가 완료되었습니다." body="구독 서비스 이용이 종료되었습니다. 환불이 필요한 경우 정책에 따라 별도로 요청해 주세요." onConfirm={() => setDialog(null)} />}
    </section></main>
  );
}

function useMemberProfileImage(member: MemberView | null) {
  const [imageUrl, setImageUrl] = useState("");
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    if (!member?.profileImageKey) {
      setImageUrl("");
      return () => { active = false; };
    }
    void getMyProfileImage().then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setImageUrl(objectUrl);
    }).catch(() => { if (active) setImageUrl(""); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [member?.profileImageKey]);
  return imageUrl;
}

function MemberAvatar({ member }: { member: MemberView | null }) {
  const imageUrl = useMemberProfileImage(member);
  return <span className="bt-member-avatar">{imageUrl ? <img src={imageUrl} alt="프로필" /> : <UserRound size={36} />}</span>;
}

function AvatarEditor({ member: providedMember = null, onChange }: { member?: MemberView | null; onChange?: (member: MemberView) => void } = {}) {
  const [loadedMember, setLoadedMember] = useState<MemberView | null>(providedMember);
  const member = providedMember ?? loadedMember;
  const imageUrl = useMemberProfileImage(member);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (providedMember) setLoadedMember(providedMember);
    else void getMyMember().then(setLoadedMember).catch(() => undefined);
  }, [providedMember]);
  return <div className="bt-avatar-editor">
    <span>{imageUrl ? <img src={imageUrl} alt="프로필" /> : <UserRound size={36} />}</span>
    <label className={uploading ? "is-disabled" : ""}>{uploading ? "변경 중" : "프로필 변경"}<input type="file" accept="image/jpeg,image/png" disabled={uploading} onChange={async (event) => {
      const file = event.currentTarget.files?.[0];
      event.currentTarget.value = "";
      if (!file) return;
      setUploading(true); setError("");
      try {
        const updated = await uploadMyProfileImage(file);
        storeMember(updated);
        setLoadedMember(updated);
        onChange?.(updated);
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "프로필 이미지를 변경하지 못했습니다.");
      } finally {
        setUploading(false);
      }
    }} /></label>
    {error ? <small role="alert">{error}</small> : null}
  </div>;
}

function SubscriptionScreen() {
  const [plans, setPlans] = useState<SubscriptionPlanView[]>([]);
  const [plan, setPlan] = useState("PLAN-03M");
  const [agreements, setAgreements] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trialOfferOpen, setTrialOfferOpen] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const agreementItems = ["개인 정보 수집 및 이용 동의", "이용 약관 동의", "결제 동의"];
  const all = agreementItems.every((item) => agreements.has(item));
  const selectedPlan = plans.find((item) => item.planCode === plan) ?? plans[0];
  useEffect(() => {
    void Promise.all([getMyMember(), listSubscriptionPlans()]).then(([member, items]) => {
      storeMember(member);
      setTrialOfferOpen(member.trialAvailable && !hasActiveSubscription(member));
      setPlans(items);
      if (!items.some((item) => item.planCode === plan) && items[0]) setPlan(items[0].planCode);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "구독권 정보를 불러오지 못했습니다."));
  }, []);
  function toggle(item: string) {
    setAgreements((current) => { const next = new Set(current); next.has(item) ? next.delete(item) : next.add(item); return next; });
  }
  async function beginPayment() {
    if (!selectedPlan || loading) return;
    sessionStorage.removeItem(POST_SUBSCRIPTION_TARGET_KEY);
    setLoading(true); setError("");
    try {
      const order = await createPaymentOrder(selectedPlan.planCode);
      const callback = new URL(window.location.origin + window.location.pathname);
      callback.searchParams.set("view", "paymentSuccess");
      const failure = new URL(window.location.origin + window.location.pathname);
      failure.searchParams.set("view", "paymentFail");
      await requestTossCardPayment({
        clientKey: order.clientKey,
        customerKey: order.customerKey,
        orderId: order.orderId,
        orderName: order.orderName,
        amount: order.amount,
        customerName: order.customerName,
        customerEmail: order.customerEmail,
        customerMobilePhone: order.customerMobilePhone,
        successUrl: callback.toString(),
        failUrl: failure.toString(),
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "결제를 시작하지 못했습니다.");
      setLoading(false);
    }
  }
  async function beginTrial() {
    if (startingTrial) return;
    setStartingTrial(true); setError("");
    try {
      await startSubscriptionTrial();
      const member = await getMyMember();
      storeMember(member);
      const target = sessionStorage.getItem(POST_SUBSCRIPTION_TARGET_KEY);
      sessionStorage.removeItem(POST_SUBSCRIPTION_TARGET_KEY);
      if (target) window.location.href = target;
      else navigate("my");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "무료 체험을 시작하지 못했습니다.");
      setTrialOfferOpen(false);
      setStartingTrial(false);
    }
  }
  return <><AuthScreen title="구독 결제" backTo="my"><div className="bt-subscription-content">
    <section><h1>구독 정보</h1><div className="bt-plan-list">{plans.map((item) => <button type="button" key={item.planCode} className={plan === item.planCode ? "is-selected" : ""} onClick={() => setPlan(item.planCode)}><span>{item.durationMonths}개월권</span><strong>{item.priceKrw.toLocaleString("ko-KR")}원</strong></button>)}</div></section>
    <section className="bt-payment-summary"><h2>결제 금액</h2><dl><div><dt>멤버십 금액</dt><dd>{selectedPlan ? `${selectedPlan.priceKrw.toLocaleString("ko-KR")}원` : "-"}</dd></div><div><dt>총 결제 금액</dt><dd>{selectedPlan ? `${selectedPlan.priceKrw.toLocaleString("ko-KR")}원` : "-"}</dd></div></dl></section>
    <section className="bt-payment-agreements"><h2>결제 동의</h2><button type="button" onClick={() => setAgreements(all ? new Set() : new Set(agreementItems))}><Checkbox checked={all} /><span>전체 동의</span></button>{agreementItems.map((item) => <button type="button" key={item} onClick={() => toggle(item)}><Checkbox checked={agreements.has(item)} /><span><b>[필수]</b> {item}</span></button>)}</section>
    <section className="bt-refund-policy"><details open><summary>구독 취소 및 환불 정책</summary><div><p>회원은 언제든지 구독취소를 할 수 있습니다.</p><p>단, 구독 취소와 결제금액의 환불은 별개의 절차이며, 환불은 아래 기준에 따라 처리됩니다.</p><h3>1. 최초 구독 결제 환불</h3><p>최초 구독 결제일로부터 7일 이내이며, 결제 이후 AI 레시피를 단 한 번도 제공받지 않은 경우 결제금액 전액을 환불받을 수 있습니다.</p><p>다음 두 가지 조건을 모두 충족해야 합니다.</p><ul><li>최초 구독 결제일로부터 7일 이내에 환불을 요청한 경우</li><li>결제 이후 AI 레시피가 한 건도 생성 또는 제공되지 않은 경우</li></ul><p>위 조건을 모두 충족하는 경우 결제금액 전액을 환불합니다.</p><h3>2. AI 레시피를 제공받은 경우</h3><p>구독 결제 후 AI 레시피가 1건 이상 생성 또는 제공된 경우 유료 서비스가 이용된 것으로 간주합니다.</p><p>AI 레시피를 제공받은 이후에는 최초 결제일로부터 7일 이내라 하더라도 단순 변심에 의한 환불은 제한될 수 있습니다.</p></div></details></section>
    {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
  </div><FullButton disabled={!all || !selectedPlan || loading} onClick={beginPayment}>{loading ? "결제 준비 중" : "결제하기"}</FullButton></AuthScreen>{trialOfferOpen ? <ActionModal
    title={<>1주일 무료 체험을<br />시작하시겠어요?</>}
    body="무료 체험은 계정당 1회 제공되며, 시작 후 7일 동안 AI 식단 추천을 이용할 수 있습니다."
    cancelLabel="취소"
    confirmLabel={startingTrial ? "처리 중" : "무료 체험 시작"}
    onCancel={() => {
      sessionStorage.removeItem(POST_SUBSCRIPTION_TARGET_KEY);
      setTrialOfferOpen(false);
    }}
    onConfirm={() => void beginTrial()}
  /> : null}</>;
}

function PaymentSuccessScreen() {
  const params = new URLSearchParams(window.location.search);
  const paymentKey = params.get("paymentKey") ?? "";
  const orderId = params.get("orderId") ?? "";
  const amount = Number(params.get("amount") ?? "0");
  const [payment, setPayment] = useState<MemberPaymentView | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!paymentKey || !orderId || !Number.isSafeInteger(amount) || amount < 1) {
      setError("결제 승인 정보가 올바르지 않습니다."); return;
    }
    void confirmPayment({ paymentKey, orderId, amount }).then(async (value) => {
      setPayment(value);
      try { const member = await getMyMember(); storeMember(member); } catch { /* Payment is already confirmed. */ }
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "결제를 승인하지 못했습니다."));
  }, [paymentKey, orderId, amount]);
  return <AuthScreen title="결제 완료"><div className="bt-payment-result">
    {error ? <><span className="is-error">!</span><h1>결제가 완료되지 않았습니다.</h1><p role="alert">{error}</p></> : payment ? <><span><Check size={34} /></span><h1>결제가 완료되었습니다.</h1><p>{payment.planName} 구독권이 등록되었습니다.</p><strong>{payment.amountKrw.toLocaleString("ko-KR")}원</strong></> : <><span>...</span><h1>결제를 확인하고 있습니다.</h1><p>잠시만 기다려 주세요.</p></>}
  </div><FullButton onClick={() => navigate(error ? "subscription" : "paymentHistory")}>{error ? "결제 화면으로" : "결제 내역 확인"}</FullButton></AuthScreen>;
}

function PaymentFailScreen() {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("orderId") ?? "";
  const code = params.get("code") ?? "PAYMENT_CANCELLED";
  const message = params.get("message") ?? "결제가 취소되었거나 완료되지 않았습니다.";
  useEffect(() => { if (orderId) void recordPaymentFailure({ orderId, code, message }).catch(() => undefined); }, [orderId, code, message]);
  return <AuthScreen title="결제 실패"><div className="bt-payment-result"><span className="is-error">!</span><h1>결제가 완료되지 않았습니다.</h1><p>{message}</p><small>{code}</small></div><FullButton onClick={() => navigate("subscription")}>다시 결제하기</FullButton></AuthScreen>;
}

function MyInfoScreen() {
  const isKakaoEntry = new URLSearchParams(window.location.search).get("source") === "kakao";
  const [member, setMember] = useState<MemberView | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { void getMyMember().then((value) => { setMember(value); setName(value.name); setPhone(formatPhone(value.phone ?? "")); }).catch((reason) => setError(reason instanceof Error ? reason.message : "회원 정보를 불러오지 못했습니다.")); }, []);
  return <AuthScreen title="내 정보 수정" backTo="my"><div className="bt-edit-content"><AvatarEditor /><h1>정보 입력</h1><div className="bt-edit-fields">
    <label><span>이름</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="이름을 입력해주세요." /></label>
    <label><span>아이디</span><input value={member?.loginId ?? "카카오 회원"} disabled /><small>*아이디는 수정이 불가합니다.</small></label>
    {!isKakaoEntry && member?.authProvider !== "KAKAO" ? <label><span>비밀번호</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력해 주세요." /><input type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} placeholder="비밀번호를 입력해 주세요." /></label> : null}
    <label><span>휴대전화 번호</span><input value={phone} onChange={(event) => setPhone(formatPhone(event.target.value))} /></label>
    <label><span>이메일</span><input value={member?.email ?? ""} disabled /><small>*이메일 수정은 고객센터에 문의해 주시기 바랍니다.</small></label>
    {error ? <p className="bt-auth-error" role="alert">{error}</p> : null}
  </div></div><FullButton disabled={!member || loading || !name || phone.replace(/\D/g, "").length < 10 || (!!password && password !== confirm)} onClick={async () => {
    setLoading(true); setError("");
    try { const value = await updateMyMember({ name, phone, password: password || undefined }); setMember(value); storeMember(value); setComplete(true); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "회원정보를 수정하지 못했습니다."); }
    finally { setLoading(false); }
  }}>{loading ? "저장 중" : "완료"}</FullButton>{complete && <ActionModal title="회원정보 수정이 완료되었습니다." onConfirm={() => navigate(isKakaoEntry ? "home" : "my")} />}</AuthScreen>;
}

const profileChipGroups = [
  { title: "건강 목표", required: true, options: ["체중 감량 (다이어트)", "근육 증가 (벌크업)", "체중 유지 및 건강 관리", "특정 질환 관리"] },
  { title: "질환", options: ["당뇨/혈당 장애", "고혈압", "이상지질혈증", "소화기 질환", "신장 질환", "해당 없음", "기타 (직접 입력)"] },
  { title: "알레르기 및 기피 식재료", required: true, options: ["해당없음", "난류 (계란)", "유제품 (우유, 치즈 등)", "견과류 (땅콩, 호두 등)", "갑각류 (새우, 게 등)", "밀 (글루텐 민감성 포함)", "대두 (콩류)", "생선 및 조개류", "복숭아, 토마토, 기타 과일류", "기타 (직접 입력)"] },
  { title: "음식 취향", required: true, options: ["한식", "양식", "중식", "일식", "아시안", "브런치/샐러드", "비세스/스무디", "채식"] },
  { title: "함께 식사하는 인원", required: true, options: ["1인", "2인", "3~4인", "5인 이상"] },
] as const;

const directInputOption = "기타 (직접 입력)";
const directInputGroups = new Set(["질환", "알레르기 및 기피 식재료"]);
const exclusiveEmptyOptions = new Map([
  ["질환", "해당 없음"],
  ["알레르기 및 기피 식재료", "해당없음"],
]);
const diningPartySizeLabels = {
  ONE: "1인",
  TWO: "2인",
  THREE_TO_FOUR: "3~4인",
  FIVE_PLUS: "5인 이상",
} as const;
const diningPartySizeCodes = Object.fromEntries(
  Object.entries(diningPartySizeLabels).map(([code, label]) => [label, code]),
) as Record<string, MemberView["diningPartySize"]>;

function profileSelectionKey(groupTitle: string, option: string) {
  return `${groupTitle}\u0000${option}`;
}

function decodeProfileValues(groupTitle: string, rawValue: string | null) {
  const group = profileChipGroups.find((item) => item.title === groupTitle);
  if (!group || !rawValue) return { values: [] as string[], directInput: "" };

  const regularOptions = (group.options as readonly string[]).filter((option) => option !== directInputOption);
  const values = regularOptions.filter((option) => rawValue.includes(option));
  let directInput = rawValue;
  [...values, directInputOption]
    .sort((left, right) => right.length - left.length)
    .forEach((value) => { directInput = directInput.split(value).join(""); });
  directInput = directInput.replace(/^[\s,]+|[\s,]+$/g, "").replace(/\s*,\s*,+/g, ", ").trim();

  if (rawValue.includes(directInputOption) || directInput) values.push(directInputOption);
  return { values, directInput };
}

function HealthProfileScreen() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [directInputs, setDirectInputs] = useState<Record<string, string>>({
    "질환": "",
    "알레르기 및 기피 식재료": "",
  });
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [gender, setGender] = useState("");
  const [activity, setActivity] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { void getMyMember().then((member) => {
    if (member.birthDate) setAge(String(Math.max(0, new Date().getFullYear() - Number(member.birthDate.slice(0, 4)))));
    setHeight(member.heightCm ? String(member.heightCm) : ""); setWeight(member.weightKg ? String(member.weightKg) : "");
    setGender(member.gender ?? ""); setActivity(member.activityLevel ?? "");
    const diningPartySizeLabel = member.diningPartySize
      ? diningPartySizeLabels[member.diningPartySize]
      : member.eatsAlone === true ? "1인" : member.profileCompletedAt ? "2인" : null;
    const storedValues: Record<string, string | null> = {
      "건강 목표": member.healthGoal,
      "질환": member.diseases,
      "알레르기 및 기피 식재료": member.allergyFoods,
      "음식 취향": member.foodPreferences,
      "함께 식사하는 인원": diningPartySizeLabel,
    };
    const nextSelected = new Set<string>();
    const nextDirectInputs = { "질환": "", "알레르기 및 기피 식재료": "" };
    profileChipGroups.forEach((group) => {
      const decoded = decodeProfileValues(group.title, storedValues[group.title]);
      decoded.values.forEach((value) => nextSelected.add(profileSelectionKey(group.title, value)));
      if (group.title in nextDirectInputs) nextDirectInputs[group.title as keyof typeof nextDirectInputs] = decoded.directInput;
    });
    setSelected(nextSelected);
    setDirectInputs(nextDirectInputs);
  }).catch((reason) => setError(reason instanceof Error ? reason.message : "프로필을 불러오지 못했습니다.")); }, []);
  function toggle(groupTitle: string, value: string) {
    setSelected((current) => {
      const next = new Set(current);
      const groupOptions = profileChipGroups.find((group) => group.title === groupTitle)?.options ?? [];
      if (groupTitle === "함께 식사하는 인원") {
        groupOptions.forEach((option) => next.delete(profileSelectionKey(groupTitle, option)));
      }
      const emptyOption = exclusiveEmptyOptions.get(groupTitle);
      if (emptyOption) {
        if (value === emptyOption) groupOptions.forEach((option) => next.delete(profileSelectionKey(groupTitle, option)));
        else next.delete(profileSelectionKey(groupTitle, emptyOption));
      }
      const key = profileSelectionKey(groupTitle, value);
      current.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
  async function saveProfile() {
    const valuesFor = (title: string) => profileChipGroups.find((group) => group.title === title)?.options.filter((value) => selected.has(profileSelectionKey(title, value))) ?? [];
    const serializedValuesFor = (title: string) => {
      const values: string[] = valuesFor(title).filter((value) => value !== directInputOption);
      if (valuesFor(title).includes(directInputOption) && directInputs[title]?.trim()) values.push(directInputs[title].trim());
      return values.join(", ") || null;
    };
    const numericAge = Number(age);
    const numericHeight = Number(height);
    const numericWeight = Number(weight);
    const missingRequiredGroup = profileChipGroups.find((group) => "required" in group && group.required && valuesFor(group.title).length === 0);
    const diningPartySize = valuesFor("함께 식사하는 인원")[0] ?? "";

    if (!age || !Number.isFinite(numericAge) || numericAge < 1 || numericAge > 120) {
      setError("나이를 1~120 사이의 숫자로 입력해 주세요."); return;
    }
    if (!height || !Number.isFinite(numericHeight) || numericHeight < 50 || numericHeight > 260) {
      setError("키를 50~260cm 사이로 입력해 주세요."); return;
    }
    if (!weight || !Number.isFinite(numericWeight) || numericWeight < 20 || numericWeight > 400) {
      setError("체중을 20~400kg 사이로 입력해 주세요."); return;
    }
    if (!gender) { setError("성별을 선택해 주세요."); return; }
    if (!activity) { setError("활동량을 선택해 주세요."); return; }
    if (missingRequiredGroup) { setError(`${missingRequiredGroup.title} 항목을 선택해 주세요.`); return; }
    if (!diningPartySize) { setError("함께 식사하는 인원을 선택해 주세요."); return; }
    const emptyDirectInputGroup = [...directInputGroups].find((title) => valuesFor(title).includes(directInputOption) && !directInputs[title]?.trim());
    if (emptyDirectInputGroup) { setError(`${emptyDirectInputGroup}의 기타 내용을 입력해 주세요.`); return; }

    setLoading(true); setError("");
    try {
      const updatedMember = await updateMyProfile({
        birthDate: `${new Date().getFullYear() - numericAge}-01-01`,
        gender: (gender || null) as MemberView["gender"], heightCm: height ? Number(height) : null,
        weightKg: weight ? Number(weight) : null, activityLevel: activity || null,
        healthGoal: serializedValuesFor("건강 목표"),
        diseases: serializedValuesFor("질환"),
        allergyFoods: serializedValuesFor("알레르기 및 기피 식재료"),
        foodPreferences: serializedValuesFor("음식 취향"),
        diningPartySize: diningPartySizeCodes[diningPartySize],
        eatsAlone: diningPartySize === "1인",
      });
      storeMember(updatedMember);
      storeProfileRequirement(false);
      const target = sessionStorage.getItem(POST_PROFILE_TARGET_KEY); sessionStorage.removeItem(POST_PROFILE_TARGET_KEY);
      if (target) {
        setLoading(false);
        const targetUrl = new URL(target, window.location.origin);
        const targetView = targetUrl.searchParams.get("view") ?? "recommend";
        targetUrl.searchParams.delete("view");
        await navigateWithRecommendationAccess(targetView, Object.fromEntries(targetUrl.searchParams.entries()));
      } else navigate("my");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "프로필을 저장하지 못했습니다."); setLoading(false); }
  }
  return <AuthScreen title="프로필 수정" backTo="my"><div className="bt-health-profile"><AvatarEditor /><h1>정보 입력</h1><div className="bt-health-numbers"><label><span className="bt-field-label">나이 <b>*</b></span><input inputMode="numeric" value={age} onChange={(event) => setAge(event.target.value.replace(/\D/g, "").slice(0, 3))} placeholder="나이를 숫자로만 입력해 주세요." /></label><label><span className="bt-field-label">키 <b>*</b></span><span><input inputMode="decimal" value={height} onChange={(event) => setHeight(event.target.value.replace(/[^\d.]/g, ""))} placeholder="키를 숫자로만 입력해 주세요." /><i>cm</i></span></label><label><span className="bt-field-label">체중 <b>*</b></span><span><input inputMode="decimal" value={weight} onChange={(event) => setWeight(event.target.value.replace(/[^\d.]/g, ""))} placeholder="체중을 숫자로만 입력해 주세요." /><i>kg</i></span></label><fieldset><legend>성별 <b>*</b></legend><div><button type="button" className={gender === "MALE" ? "is-selected" : ""} onClick={() => setGender("MALE")}>남성</button><button type="button" className={gender === "FEMALE" ? "is-selected" : ""} onClick={() => setGender("FEMALE")}>여성</button></div></fieldset><label><span className="bt-field-label">활동량 <b>*</b></span><select value={activity} onChange={(event) => setActivity(event.target.value)}><option value="" disabled>선택</option><option>거의 활동하지 않음</option><option>가벼운 활동 (주 1~3회 가벼운 운동)</option><option>보통 활동 (주 3~5회 중간 강도의 운동)</option><option>많은 활동 (주 6~7회 강도 높은 운동)</option><option>매우 많은 활동</option></select></label></div>{profileChipGroups.map((group) => { const directInputSelected = selected.has(profileSelectionKey(group.title, directInputOption)); return <fieldset className="bt-chip-field" key={group.title}><legend>{group.title}{"required" in group && group.required && <> <b>*</b></>}</legend><div className="bt-chip-options">{group.options.map((option) => <button type="button" key={option} className={selected.has(profileSelectionKey(group.title, option)) ? "is-selected" : ""} onClick={() => toggle(group.title, option)}>{option}</button>)}</div>{directInputGroups.has(group.title) && directInputSelected ? <input className="bt-chip-direct-input" value={directInputs[group.title] ?? ""} onChange={(event) => setDirectInputs((current) => ({ ...current, [group.title]: event.target.value }))} placeholder={`${group.title}을 직접 입력해 주세요.`} aria-label={`${group.title} 기타 직접 입력`} /> : null}</fieldset>; })}{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}</div><FullButton disabled={loading} onClick={saveProfile}>{loading ? "저장 중" : "완료"}</FullButton></AuthScreen>;
}

function RecommendationHistoryScreen() {
  const source = new URLSearchParams(window.location.search).get("from") === "recommend" ? "recommend" : "my";
  const [history, setHistory] = useState<RecommendationListItem[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    void listRecommendations(50).then(setHistory).catch((reason) => setError(reason instanceof Error ? reason.message : "추천 이력을 불러오지 못했습니다."));
  }, []);
  return <AuthScreen title="식단 추천 내역" backTo={source}><div className="bt-history-page"><p>총 {history.length} 건</p>{error ? <span role="alert">{error}</span> : null}{history.map((item) => <button key={item.recommendationCode} type="button" onClick={() => navigate("recommendResults", { from: "recommendHistory", historyFrom: source, code: item.recommendationCode })}><strong>{formatRecommendationDate(item.requestedAt)}</strong><span>식재료 : {item.ingredients}</span><small>추천 레시피 {item.recipeCount}개</small></button>)}</div></AuthScreen>;
}

function PaymentHistoryScreen() {
  const [payments, setPayments] = useState<MemberPaymentView[]>([]); const [error, setError] = useState("");
  useEffect(() => { void listMemberPayments().then(setPayments).catch((reason) => setError(reason instanceof Error ? reason.message : "결제 내역을 불러오지 못했습니다.")); }, []);
  return <AuthScreen title="결제 내역" backTo="my"><div className="bt-history-page"><p>총 {payments.length} 건</p>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}{payments.map((item) => <button key={item.paymentCode} type="button" onClick={() => navigate("paymentDetail", { payment: item.paymentCode })}><strong>{item.planName}</strong><em>{item.amountKrw.toLocaleString("ko-KR")}원</em><span>{displayDate(item.paidAt)}</span><small>상세보기</small></button>)}</div></AuthScreen>;
}

function PaymentDetailScreen() {
  const code = new URLSearchParams(window.location.search).get("payment") ?? "";
  const [payment, setPayment] = useState<MemberPaymentView | null>(null); const [error, setError] = useState("");
  useEffect(() => { if (code) void getMemberPayment(code).then(setPayment).catch((reason) => setError(reason instanceof Error ? reason.message : "결제 상세를 불러오지 못했습니다.")); }, [code]);
  const status = payment?.status === "DONE" ? "결제완료" : payment?.status === "FAILED" ? "결제실패" : payment?.status === "CANCELLED" ? "결제취소" : payment?.status ?? "-";
  return <AuthScreen title="결제 상세" backTo="paymentHistory"><div className="bt-detail-table"><h1>구독 결제 정보</h1>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}<dl><div><dt>상품명</dt><dd>{payment?.planName ?? "-"}</dd></div><div><dt>결제금액</dt><dd>{payment ? `${payment.amountKrw.toLocaleString("ko-KR")}원` : "-"}</dd></div><div><dt>결제일</dt><dd>{displayDate(payment?.paidAt)}</dd></div><div><dt>결제수단</dt><dd>{payment?.method ?? "-"}</dd></div><div><dt>상태</dt><dd>{status}</dd></div></dl></div></AuthScreen>;
}

function InquiryListScreen() {
  const [filter, setFilter] = useState("전체");
  const [items, setItems] = useState<MemberInquiryView[]>([]); const [error, setError] = useState("");
  const categoryLabels: Record<MemberInquiryView["category"], string> = { AI_RECOMMENDATION: "AI식단 추천 문의", RECIPE: "레시피 문의", SERVICE: "서비스 이용 문의", OTHER: "기타 문의" };
  const filters = ["전체", ...Object.values(categoryLabels)];
  useEffect(() => { void listMemberInquiries().then(setItems).catch((reason) => setError(reason instanceof Error ? reason.message : "문의 내역을 불러오지 못했습니다.")); }, []);
  const filtered = filter === "전체" ? items : items.filter((item) => categoryLabels[item.category] === filter);
  return <AuthScreen title="1:1 문의" backTo="my"><div className="bt-inquiry-content"><div className="bt-filter-scroll">{filters.map((item) => <button type="button" key={item} className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)}>{item}</button>)}</div><button type="button" className="bt-inquiry-cta" onClick={() => navigate("inquiryWrite")}><Headphones size={26} /><strong>도움이 필요하신가요?</strong><u>문의하기</u></button><p>총 {filtered.length} 건</p>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}<div className="bt-inquiry-list">{filtered.map((item) => <button type="button" key={item.inquiryCode} onClick={() => navigate("inquiryDetail", { inquiry: item.inquiryCode })}><span>{item.status === "ANSWERED" ? "답변 완료" : "답변 대기"}</span><strong>[{categoryLabels[item.category]}] {item.title}</strong><small>{displayDate(item.createdAt)}</small></button>)}</div></div></AuthScreen>;
}

function InquiryWriteScreen() {
  const [category, setCategory] = useState(""); const [title, setTitle] = useState(""); const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  return <AuthScreen title="문의하기" backTo="inquiries"><div className="bt-inquiry-form"><label>문의 유형 <b>*</b><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">선택</option><option value="AI_RECOMMENDATION">AI식단 추천 문의</option><option value="RECIPE">레시피 문의</option><option value="SERVICE">서비스 이용 문의</option><option value="OTHER">기타 문의</option></select></label><label>문의 제목 <b>*</b><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="문의 제목을 작성해 주세요." /></label><label>문의 내용 <b>*</b><textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="문의하실 내용을 입력해 주세요." /></label>{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}</div><FullButton disabled={!category || !title.trim() || !content.trim() || loading} onClick={async () => {
    setLoading(true); setError("");
    try { await createMemberInquiry({ category: category as MemberInquiryView["category"], title: title.trim(), content: content.trim() }); navigate("inquiries"); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "문의를 등록하지 못했습니다."); setLoading(false); }
  }}>{loading ? "등록 중" : "완료"}</FullButton></AuthScreen>;
}

function InquiryDetailScreen() {
  const code = new URLSearchParams(window.location.search).get("inquiry") ?? "";
  const [inquiry, setInquiry] = useState<MemberInquiryView | null>(null); const [error, setError] = useState("");
  useEffect(() => { if (code) void getMemberInquiry(code).then(setInquiry).catch((reason) => setError(reason instanceof Error ? reason.message : "문의 상세를 불러오지 못했습니다.")); }, [code]);
  const labels: Record<MemberInquiryView["category"], string> = { AI_RECOMMENDATION: "AI식단 추천 문의", RECIPE: "레시피 문의", SERVICE: "서비스 이용 문의", OTHER: "기타 문의" };
  return <AuthScreen title="문의 상세" backTo="inquiries"><div className="bt-inquiry-detail">{error ? <p className="bt-auth-error" role="alert">{error}</p> : null}<span>{inquiry?.status === "ANSWERED" ? "답변 완료" : "답변 대기"}</span><h1>{inquiry ? `[${labels[inquiry.category]}] ${inquiry.title}` : "문의 정보를 불러오고 있습니다."}</h1><time>{displayDate(inquiry?.createdAt)}</time><section><h2>문의 내용</h2><p>{inquiry?.content ?? "-"}</p></section><section><h2>답변</h2><p>{inquiry?.answer ?? "답변을 준비하고 있습니다."}</p></section></div></AuthScreen>;
}

function RouteContent() {
  const view = new URLSearchParams(window.location.search).get("view");
  switch (view) {
    case "kakaoCallback": return <KakaoCallbackScreen />;
    case "signup": return <SignupTermsScreen />;
    case "signupProfile": return <SignupProfileScreen />;
    case "signupEmail": return <SignupEmailScreen />;
    case "signupVerify": return <SignupVerifyScreen />;
    case "findId": return <EmailLookupScreen mode="id" />;
    case "findPassword": return <EmailLookupScreen mode="password" />;
    case "findIdVerify": return <LookupVerifyScreen mode="id" />;
    case "findPasswordVerify": return <LookupVerifyScreen mode="password" />;
    case "findIdResult": return <FindIdResultScreen />;
    case "resetPassword": return <ResetPasswordScreen />;
    case "home": return <HomeScreen />;
    case "recommend": return <RecommendationScreen />;
    case "recommendProfile": return <RecommendationUploadScreen />;
    case "recommendImageLoading": return <AnalysisLoadingScreen type="image" />;
    case "recommendIngredients": return <RecommendationIngredientsScreen />;
    case "recommendFinalLoading": return <AnalysisLoadingScreen type="recommendation" />;
    case "recommendResults": return <RecommendationResultsScreen />;
    case "recipeDetail": return <RecipeDetailScreen />;
    case "savedRecipes": return <SavedRecipesScreen />;
    case "my": return <MyScreen />;
    case "subscription": return <SubscriptionScreen />;
    case "paymentSuccess": return <PaymentSuccessScreen />;
    case "paymentFail": return <PaymentFailScreen />;
    case "myInfo": return <MyInfoScreen />;
    case "healthProfile": return <HealthProfileScreen />;
    case "recommendHistory": return <RecommendationHistoryScreen />;
    case "paymentHistory": return <PaymentHistoryScreen />;
    case "paymentDetail": return <PaymentDetailScreen />;
    case "inquiries": return <InquiryListScreen />;
    case "inquiryWrite": return <InquiryWriteScreen />;
    case "inquiryDetail": return <InquiryDetailScreen />;
    default: return <LoginScreen />;
  }
}

function App() {
  return <><RouteContent /><ProfileGuardModal /><SubscriptionGuardModal /></>;
}

createRoot(document.getElementById("root")!).render(<StrictMode><App /></StrictMode>);
